import { WEEKDAYS, dayIntervals, parseOpeningHours, type Weekday } from "@majourneys/shared";

const SCHEMA_DAY: Record<Weekday, string> = {
  mon: "https://schema.org/Monday",
  tue: "https://schema.org/Tuesday",
  wed: "https://schema.org/Wednesday",
  thu: "https://schema.org/Thursday",
  fri: "https://schema.org/Friday",
  sat: "https://schema.org/Saturday",
  sun: "https://schema.org/Sunday",
};

export interface OpeningHoursSpecification {
  "@type": "OpeningHoursSpecification";
  dayOfWeek: string[];
  opens: string;
  closes: string;
}

/**
 * A venue's stored week as schema.org OpeningHoursSpecification nodes, for
 * the page's JSON-LD. Days that share a service are grouped into one node;
 * a two-service day gets a node per service. A close before the open means
 * after midnight, which Google reads as written (opens 19:00, closes 02:00).
 * A close at exactly midnight is emitted as 23:59, the value Google documents
 * for "until the end of the day". Unknown or malformed hours give [] so the
 * page never states hours it does not have.
 */
export function openingHoursToSchemaOrg(stored: unknown): OpeningHoursSpecification[] {
  const week = parseOpeningHours(stored);
  if (!week) return [];
  const groups = new Map<string, OpeningHoursSpecification>();
  for (const day of WEEKDAYS) {
    for (const { open, close } of dayIntervals(week[day])) {
      const closes = close === "00:00" ? "23:59" : close;
      const key = `${open}-${closes}`;
      const spec = groups.get(key);
      if (spec) {
        spec.dayOfWeek.push(SCHEMA_DAY[day]);
      } else {
        groups.set(key, {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [SCHEMA_DAY[day]],
          opens: open,
          closes,
        });
      }
    }
  }
  return [...groups.values()];
}
